import React, { useContext, useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import TermsCheckbox from '../../../../components/TermsCheckbox';
import { CryptodraftContext } from '../../../../contexts/card/cryptodraft/context';
import { exchange } from '../../../../utilities/constants';
import { formatNumber, formatNumberWithComma } from '../../../../utilities/formatHelpers';
import { showAlert } from '../../../../utilities/notifications';
import { useRate } from '../../../../utilities/sharedHooks/rates';
import { BANK_REQUESTS_COINS } from '../../../Accounts/constants';
import { CARD_CREATE_CRYPTODRAFT } from '../../mutations';
import { CARD_LINKED_WALLETS } from '../../queries';
import { cryptodraftScreens, forceUpdateOptions } from '../constants';
import MainBodyLayout from '../partials/MainBodyLayout';
import { FLOWS, sendStats } from '../../../../services/mixpanel';
import { MXP_FC_GET_CONTINUE } from '../../mixpanel-events';

const CryptodraftReviewOffer = () => {
  const { t: translate } = useTranslation();

  const [topLtv, setTopLtv] = useState(0);

  const history = useHistory();

  const { state, openScreen, forceUpdateAction } = useContext(CryptodraftContext);

  const [termAndConditionChecked, setTermAndConditionChecked] = useState(false);

  const isUSUser = state?.user?.isUS;
  const coin = state?.selectedWallet?.coin;
  const fiatCurrency = state?.fiatCurrency;
  const principalCurrency = state?.principalCurrency;
  const cryptodraftTierLTV = state?.cryptodraftTierLTV;

  const { refetch: getLinkedWallet } = useQuery(CARD_LINKED_WALLETS, {
    enabled: false,
  });

  const { mutate: createCryptodraft, isLoading } = useMutation(CARD_CREATE_CRYPTODRAFT, {
    onSettled: (data, error) => {
      if (error) {
        showAlert({ message: error.message, type: 'danger' });
      } else {
        showAlert({ message: translate('create_cryptodraft_success_msg'), type: 'success' });
        forceUpdateAction(forceUpdateOptions.DETAILS, true);
        getLinkedWallet();
        history.push('/dashboard/card/details');
      }
    },
  });

  const {
    cryptodraft_ltv_ranges: _cryptodraftLtvRanges,
    cryptodraft_us_ltv_ranges: _cryptodraftUSLtvRanges,
  } = state?.cryptodraftSettings;

  const cryptodraftLtvRanges = useMemo(
    () => (isUSUser ? _cryptodraftUSLtvRanges : _cryptodraftLtvRanges),
    [_cryptodraftLtvRanges, _cryptodraftUSLtvRanges, isUSUser]
  );

  const rate = useRate(coin, fiatCurrency);

  // just in case user displayCurrency GBP and need to revert to USDT
  const collateralToUSDRate = useRate(coin, BANK_REQUESTS_COINS.USD);

  const collateralAmountInFiatValue =
    fiatCurrency?.toLowerCase() === BANK_REQUESTS_COINS.GBP
      ? state?.collateralAmount * collateralToUSDRate
      : state?.collateralAmount * rate;

  useEffect(() => {
    const ltvRangesValues = cryptodraftLtvRanges?.split(',').map((item) => item.split('-'));
    if (ltvRangesValues) {
      const ltvRanges = [].concat(...ltvRangesValues);
      const topLtvValue = Math.max.apply(null, ltvRanges);
      setTopLtv(topLtvValue);
    }
    const ltvRanges = [].concat(...ltvRangesValues);
    const topLtvValue = Math.max.apply(null, ltvRanges);
    setTopLtv(topLtvValue);
  }, [state?.collateralAmount, rate, cryptodraftLtvRanges]);

  const topLtvValue = useMemo(() => {
    if (cryptodraftTierLTV) {
      return cryptodraftTierLTV > topLtv ? cryptodraftTierLTV : topLtv;
    }
    return topLtv;
  }, [topLtv, cryptodraftTierLTV]);

  const zeroSpendingLimitLTV = useMemo(() => {
    if (cryptodraftTierLTV) {
      return cryptodraftTierLTV;
    }
    return 10;
  }, [cryptodraftTierLTV]);

  return (
    <MainBodyLayout
      withBack
      withInfoIcon
      onPrevClick={() => openScreen(cryptodraftScreens.CALCULATOR)}
      title={translate('cryptodraft_activate_review_offer_label')}>
      <div className="mt-5">
        <b className="text-dark text-f4 p-0">{`
          ${formatNumberWithComma(
            formatNumber(
              formatNumber(collateralAmountInFiatValue, exchange.FIAT_VALUE) *
                (zeroSpendingLimitLTV / 100),
              exchange.FIAT_VALUE
            )
          )}
        ${principalCurrency.toUpperCase()}`}</b>
      </div>
      <div className="mt-2">
        <p className="text-dark text-f5 p-0">
          {translate('cryptodraft_review_zero_spending_limit_title')}
        </p>
      </div>
      <div className="mt-2">
        <p className="text-shadow text-f6 p-0">
          {translate('cryptodraft_review_zero_spending_limit_subtitle').replaceAll(
            '__LTV__',
            zeroSpendingLimitLTV
          )}
        </p>
      </div>
      <div className="mt-4">
        <b className="text-dark text-f4 p-0">{`
          ${formatNumberWithComma(
            formatNumber(
              formatNumber(collateralAmountInFiatValue, exchange.FIAT_VALUE) * (topLtvValue / 100),
              exchange.FIAT_VALUE
            )
          )}
         ${principalCurrency.toUpperCase()}`}</b>
      </div>
      <div className="mt-2">
        <p className="text-dark text-f5 p-0">
          {translate('cryptodraft_review_total_spending_limit_title')}
        </p>
      </div>
      <div className="mt-2">
        <p className="text-shadow text-f6 p-0">
          {translate('cryptodraft_review_total_spending_limit_subtitle').replace(
            '%d',
            `${topLtvValue}%`
          )}
        </p>
      </div>
      <div className="mb-4 mt-5 rounded-extra bg-lighter p-3">
        <div className="d-flex flex-row">
          <p className="text-dark text-f5 d-flex align-items-center">
            {translate('cryptodraft_review_total_title')}
          </p>
          <span className="text-right flex-fill">
            <b className="text-f5 m-0">
              {formatNumber(state?.collateralAmount, exchange.CRYPTO_VALUE)} {coin?.toUpperCase()}
            </b>
            <p className="text-shadow text-f6 m-0">
              {formatNumberWithComma(
                formatNumber(state?.collateralAmount * rate, exchange.FIAT_VALUE)
              )}{' '}
              {fiatCurrency.toUpperCase()}
            </p>
          </span>
        </div>
      </div>
      <div className="mt-2">
        <TermsCheckbox
          checked={termAndConditionChecked}
          onChange={() => setTermAndConditionChecked((prev) => !prev)}
          contentUrlKey="content_url_cryptodraft_terms"
          showRightToInfo={false}
          label="cryprodraft_terms_checkbox_label"
          trailing="cryptodraft_terms_checkbox_trailing_label"
        />
      </div>
      <div className="btn-container">
        <button
          type="button"
          className="btn py-3 btn-primary w-100"
          loading={isLoading ? 'true' : 'false'}
          disabled={!termAndConditionChecked}
          onClick={() => {
            sendStats(MXP_FC_GET_CONTINUE, {}, FLOWS.FC);
            createCryptodraft({
              amount: state?.collateralAmount,
              coin,
            });
          }}>
          {translate('continue_btn_title')}
        </button>
      </div>
    </MainBodyLayout>
  );
};

export default CryptodraftReviewOffer;
